import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getExecutions } from '../api/executions';
import { ExecutionResponse } from '../types';
import { STATUS_DOT } from '../lib/executionStatus';


interface Props {
  flowId: string;
  selectedId: string | null;
  compareId: string | null;
  onSelect: (id: string | null) => void;
  onSelectCompare: (id: string | null) => void;
}

export function ExecutionPicker({ flowId, selectedId, compareId, onSelect, onSelectCompare }: Props) {
  const [open, setOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const { data } = useQuery({
    queryKey: ['executions', { flowId, picker: true }],
    queryFn: () => getExecutions({ flowId, pageSize: 20, topLevelOnly: true }),
    enabled: open,
  });

  const executions = data?.items ?? [];
  const selected = executions.find(e => e.id === selectedId) ?? null;

  function label(exec: ExecutionResponse | null): string {
    if (!exec) return 'none';
    const status = exec.status;
    const time = new Date(exec.startedAt).toLocaleTimeString();
    return `${status} · ${time}`;
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex h-8 items-center gap-1.5 rounded-md border border-input bg-card px-2.5 text-xs shadow-sm hover:bg-accent"
        title="View an execution overlaid on this flow"
      >
        <span className="text-muted-foreground">Execution:</span>
        {selected ? (
          <>
            <span className={`w-2 h-2 rounded-full ${STATUS_DOT[selected.status] ?? 'bg-muted-foreground/30'}`} />
            <span className="font-mono">{label(selected)}</span>
          </>
        ) : (
          <span className="text-muted-foreground">none</span>
        )}
        {compareId && <span className="text-tone-violet" title="Compare mode active">⇄</span>}
        <span className="text-muted-foreground">▾</span>
      </button>

      {open && (
        <div className="absolute left-0 mt-1.5 w-80 bg-popover border border-border rounded-lg shadow-xl z-30 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="text-xs font-medium">
              {compareMode ? 'Pick compare' : 'Recent executions'}
            </span>
            <button
              onClick={() => setCompareMode(m => !m)}
              className="text-xs text-primary hover:underline"
            >
              {compareMode ? 'Cancel' : 'Compare…'}
            </button>
          </div>
          {!compareMode && (
            <button
              onClick={() => { onSelect(null); onSelectCompare(null); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-xs text-muted-foreground hover:bg-muted border-b border-border"
            >
              Clear
            </button>
          )}
          {!data && (
            <p className="px-3 py-3 text-xs text-muted-foreground">Loading…</p>
          )}
          {data && executions.length === 0 && (
            <p className="px-3 py-3 text-xs text-muted-foreground">No executions yet for this flow.</p>
          )}
          {executions.map(e => {
            const isSelected = compareMode ? e.id === compareId : e.id === selectedId;
            return (
              <button
                key={e.id}
                onClick={() => {
                  if (compareMode) {
                    onSelectCompare(e.id);
                    setCompareMode(false);
                  } else {
                    onSelect(e.id);
                  }
                  setOpen(false);
                }}
                disabled={!compareMode && selectedId === e.id ? false : compareMode && e.id === selectedId}
                className={`block w-full text-left px-3 py-2 text-xs border-b border-border hover:bg-muted ${isSelected ? 'bg-primary/10' : ''} disabled:opacity-40`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[e.status] ?? 'bg-muted-foreground/30'}`} />
                  <span className=" font-medium">{e.status}</span>
                  {e.label && <span className="text-muted-foreground truncate">{e.label}</span>}
                </div>
                <div className="text-muted-foreground mt-0.5 font-mono">
                  {new Date(e.startedAt).toLocaleString()}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
